import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const RESV_STATUS_CLS = {
  active: "bg-signal-amber/15 text-signal-amber",
  consumed: "bg-signal-green/15 text-signal-green",
  expired: "bg-muted text-muted-foreground",
  released: "bg-muted text-muted-foreground",
};

export default function ReservationsTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const role = user?.role;
  const canRequest = ["admin", "warehouse_manager", "sales"].includes(role);
  const canApprove = ["admin", "warehouse_manager"].includes(role);

  const load = () => api.get("/reservations").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (rid, endpoint, label) => {
    try {
      await api.post(`/reservations/${rid}/${endpoint}`, {});
      toast.success(label);
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid="reservations-tab">
      <table className="w-full text-sm" data-testid="reservations-table">
        <thead><tr className="border-b border-border text-left">
          {["ID", "Order", "Customer", "SKU", "Qty", "Type", "Expiry", "Location", "Reserved By", "Release Request", "Status", ""].map((h) => (
            <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>))}
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reservation_id} className="border-b border-border/50" data-testid={`reservation-${r.reservation_id}`}>
              <td className="px-4 py-3 metric text-xs">{r.reservation_id}</td>
              <td className="px-4 py-3"><button className="metric text-primary underline" onClick={() => navigate(`/orders/${r.bill_no}`)}>{r.bill_no}</button></td>
              <td className="px-4 py-3">{r.customer}</td>
              <td className="px-4 py-3 metric">{r.sku}</td>
              <td className="px-4 py-3 metric">{r.quantity.toLocaleString()}</td>
              <td className="px-4 py-3">
                {(r.reservation_type || "hard") === "soft" ? (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-signal-blue/15 text-signal-blue">Soft</span>
                ) : <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Hard</span>}
              </td>
              <td className="px-4 py-3 metric text-xs">{r.expires_at ? fmtDT(r.expires_at) : "—"}</td>
              <td className="px-4 py-3 metric text-xs">{r.location || "—"}</td>
              <td className="px-4 py-3 text-xs">{r.reserved_by}</td>
              <td className="px-4 py-3 text-xs">{r.release_request ? `${r.release_request.by} (${r.release_request.role})` : "—"}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                  RESV_STATUS_CLS[r.status] || "bg-muted text-muted-foreground"
                }`} data-testid={`reservation-status-${r.reservation_id}`}>{r.status}</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {r.status === "active" && !r.release_request && canRequest && (
                  <Button size="sm" variant="outline" onClick={() => act(r.reservation_id, "release-request", "Release requested")} data-testid={`release-request-${r.reservation_id}`}>Request Release</Button>)}
                {r.status === "active" && r.release_request && canApprove && r.release_request.by_id !== user?.user_id && (
                  <Button size="sm" variant="outline" onClick={() => act(r.reservation_id, "release-approve", "Reservation released")} data-testid={`release-approve-${r.reservation_id}`}>Approve Release</Button>)}
                {r.status === "active" && r.release_request && r.release_request.by_id === user?.user_id && role !== "admin" && (
                  <span className="text-[10px] font-mono text-muted-foreground">AWAITING APPROVAL</span>)}
                {r.status === "active" && role === "admin" && (
                  <Button size="sm" variant="ghost" className="text-signal-red" onClick={() => act(r.reservation_id, "release", "Released directly")} data-testid={`release-direct-${r.reservation_id}`}>Direct Release</Button>)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No reservations</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
